import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { GridCustomColDef } from '../../types';
import { getDate, getFormattedDate } from '../../../utils/helpers';
import { Association, Ticket } from '../../../store/Ticket';
import { DetailViewCard } from '../CardWithTitle/DetailViewCard';
import { isData, isObserver } from '../../../store/User';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { NewTicketDialog } from './NewTicketDialog/NewTicketDialog';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { RpmAccount } from '../../../store/RpmAccount';
import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { Asset } from '../../../store/Asset';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { NewReferenceTicketDialog } from './NewTicketDialog/NewReferenceTicketDialog';

export interface TicketMiniTableParams {
  id: string;
  fetchFunction: (id: string) => Promise<Ticket[] | undefined>;
  association?: Association;
  showCreate: boolean;
  objectData?: Asset | RpmAccount | ProvisionalAsset;
  objectType: 'asset' | 'provisionalAsset' | 'rpmAccount' | 'customer';
  objectLinkId?: string;
  objectLinkDisplay?: string;
  drawer?: boolean;
}

export const TicketMiniTable = ({
  fetchFunction,
  id,
  association,
  showCreate,
  objectData,
  objectType,
  objectLinkId,
  objectLinkDisplay,
  drawer = false
}: TicketMiniTableParams) => {
  const [isTicketFormOpen, setIsTicketFormOpen] = useState<boolean>(false);
  const [isReferenceTicketFormOpen, setIsReferenceTicketFormOpen] = useState<boolean>(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFetch = async (withLoading = true) => {
    if (withLoading) setLoading(true);
    const t = await fetchFunction(id);
    if (t) setTickets(t);
    if (withLoading) setLoading(false);
  };

  useEffect(() => {
    if (id) handleFetch();
  }, [id]);

  useEffect(() => {
    if (!isReferenceTicketFormOpen && id) {
      handleFetch(false);
    }
  }, [isReferenceTicketFormOpen]);

  const tableCols: GridCustomColDef[] = [
    {
      type: 'singleSelect',
      flex: 1,
      width: 75,
      field: 'number',
      headerName: 'Number',
      renderCell: (params: GridCellParams) => (
        <Link
          className="text-indigo"
          title="Fullscreen"
          to={`/ticket/${params.row.id}`}>
          <div className="flex items-center">
            <Icon
              className="p-3 mr-1"
              type="ticketMed"
            />
            <TextSpan
              color="indigo"
              size="sm14">
              {params.value as string}
            </TextSpan>
          </div>
        </Link>
      )
    },
    {
      field: 'inquiryType',
      headerName: 'Inquiry Type',
      type: 'singleSelect',
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      width: 75,
      flex: 1
    },
    {
      field: 'resolutionType',
      headerName: 'Resolution Type',
      width: 170,
      flex: 1
    },
    {
      headerName: 'Status Date',
      field: 'updatedAt',
      type: 'singleSelect',
      valueFormatter: (value) => {
        return getFormattedDate(value, 'LLL');
      },
      valueGetter: (value) => {
        return getDate(value).getTime();
      },
      flex: 1
    }
  ];

  const cardActions = () => {
    return isObserver() || isData() || !showCreate ? (
      <></>
    ) : (
      <div className="flex flex- items-center space-x-2">
        <button
          className="flex items-center space-x-2"
          onClick={() => setIsTicketFormOpen(true)}
          data-cy="new-ticket-button"
          data-testid="new-ticket-button">
          <Icon type="plus" />
          <TextSpan
            color="indigo"
            size="sm14">
            New Ticket
          </TextSpan>
        </button>
        <button
          className="flex items-center space-x-2 !ml-4"
          onClick={() => setIsReferenceTicketFormOpen(true)}
          data-cy="new-ticket-button"
          data-testid="add-ticket-button">
          <Icon type="plus" />
          <TextSpan
            color="indigo"
            size="sm14">
            Reference
          </TextSpan>
        </button>
      </div>
    );
  };

  return (
    <>
      {loading && <ComponentSpinner label="Loading data..." />}
      {!loading && (
        <div
          className="w-full"
          data-testid="ticket-mini-table-container">
          <DetailViewCard
            cardActions={cardActions()}
            enableCollapse
            key="mini_tickets_table"
            title="Tickets">
            <BasicDataGrid
              sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
              className="flex"
              rows={tickets}
              columns={tableCols}
              dataTestId="ticket-mini-data-grid"
              sortModel={[{ field: 'updatedAt', sort: 'desc' }]}
            />
          </DetailViewCard>
        </div>
      )}
      {showCreate && objectData && (
        <>
          <NewTicketDialog
            open={isTicketFormOpen}
            setOpen={setIsTicketFormOpen}
            association={association}
            objectData={objectData}
            objectType={objectType}
            objectLinkId={objectLinkId}
            objectLinkDisplay={objectLinkDisplay}
            drawer={drawer}
          />
          <NewReferenceTicketDialog
            open={isReferenceTicketFormOpen}
            association={association}
            setOpen={setIsReferenceTicketFormOpen}
            objectData={objectData}
            objectType={objectType}
          />
        </>
      )}
    </>
  );
};
